import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
// import { useAuth } from '../contexts/AuthContext'
import { recruiterApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { AlertCircle, ArrowRight, CheckCircle2, Clock, FileText, GraduationCap, Briefcase as BriefcaseIcon, Award, Target, Shield, ChevronDown, ChevronUp, ScrollText } from 'lucide-react'

interface CandidateDetails {
  candidate: {
    id: number
    uuid: string
    years_of_experience: number | null
    properties: {
      user_details?: {
        name?: string
        phone_number?: string
        email?: string
      }
      candidate_description?: {
        full_prolog?: string
      }
      work_experience?: Array<{
        years?: string
        job_title?: string
        company?: string
        description?: string
      }>
      skills?: string[]
      education?: string[]
      military_service?: {
        role?: string
        description?: string
      }
      professional_goals?: string
    } | null
  }
  sent_form: {
    uuid: string
    candidate_name: string
    job_description: string | null
    review: string | null
    score: number | null
    complete: number
    questions: Array<any>
    transcript: string | null
    created_at: string
    updated_at: string
  }
}

export default function CandidateDetailsPage() {
  // const { recruiter } = useAuth()
  const { sentFormUuid } = useParams<{ sentFormUuid: string }>()
  const navigate = useNavigate()
  const [details, setDetails] = useState<CandidateDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['review']))

  useEffect(() => {
    if (!sentFormUuid) {
      setError('מזהה טופס לא נמצא')
      setIsLoading(false)
      return
    }

    const fetchDetails = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await recruiterApi.getSentFormCandidateDetails(sentFormUuid)
        
        if (response.status === 'success') {
          setDetails(response)
        } else {
          setError('שגיאה בטעינת פרטי המועמד')
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'שגיאה בטעינת פרטי המועמד')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDetails()
  }, [sentFormUuid])

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !details) {
    return (
      <div className="bg-background p-6 space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error || 'פרטי מועמד לא נמצאו'}</p>
            </div>
          </CardContent>
        </Card>
        <Button onClick={() => navigate('/sentforms')} variant="outline">
          <ArrowRight className="h-4 w-4 mr-2" />
          חזור לרשימת שאלונים שנשלחו
        </Button>
      </div>
    )
  }

  const { candidate, sent_form } = details
  const properties = candidate.properties

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }

  const isExpanded = (sectionId: string) => expandedSections.has(sectionId)

  return (
    <div className="bg-background p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">פרטי מועמד</h1>
        </div>
        <Button onClick={() => navigate('/sentforms')} variant="outline">
          <ArrowRight className="h-4 w-4 mr-2" />
          חזור לרשימה
        </Button>
      </div>

      {/* CV Properties */}
      {properties && (
        <>
          {/* Combined Candidate Details Card */}
          {(properties.candidate_description?.full_prolog ||
            (properties.work_experience && properties.work_experience.length > 0) ||
            (properties.skills && properties.skills.length > 0) ||
            (properties.education && properties.education.length > 0) ||
            (properties.military_service && (properties.military_service.role || properties.military_service.description))) && (
            <Card>
              <CardHeader>
                <CardTitle>פרטי המועמד</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                {/* Candidate Description */}
                {properties.candidate_description?.full_prolog && (
                  <div className={properties.work_experience?.length || properties.skills?.length || properties.education?.length || properties.military_service ? "border-b border-border" : ""}>
                    <button
                      onClick={() => toggleSection('description')}
                      className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity mb-2 mt-2"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <FileText className="h-5 w-5" />
                        תיאור מועמד
                      </div>
                      {isExpanded('description') ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded('description') && (
                      <div className="mt-2">
                        <p className="text-sm whitespace-pre-wrap">{properties.candidate_description.full_prolog}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Work Experience */}
                {properties.work_experience && properties.work_experience.length > 0 && (
                  <div className={(properties.skills?.length || properties.education?.length || properties.military_service) ? "border-b border-border" : ""}>
                    <button
                      onClick={() => toggleSection('work_experience')}
                      className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity mb-2 mt-2"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <BriefcaseIcon className="h-5 w-5" />
                        ניסיון תעסוקתי
                      </div>
                      {isExpanded('work_experience') ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded('work_experience') && (
                      <div className="mt-2 space-y-4">
                        {properties.work_experience.map((exp, index) => (
                          <div key={index} className="border-l-2 border-primary pl-4 pb-4 last:pb-0">
                            {exp.years && (
                              <Badge variant="default" className="mb-2">{exp.years}</Badge>
                            )}
                            {exp.job_title && (
                              <h3 className="font-semibold text-lg">{exp.job_title}</h3>
                            )}
                            {exp.company && (
                              <p className="text-muted-foreground">{exp.company}</p>
                            )}
                            {exp.description && (
                              <p className="text-sm mt-2 whitespace-pre-wrap">{exp.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Skills */}
                {properties.skills && properties.skills.length > 0 && (
                  <div className={(properties.education?.length || properties.military_service) ? "border-b border-border" : ""}>
                    <button
                      onClick={() => toggleSection('skills')}
                      className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity mb-2 mt-2"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <Award className="h-5 w-5" />
                        כישורים
                      </div>
                      {isExpanded('skills') ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded('skills') && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {properties.skills.map((skill, index) => (
                            <Badge key={index} variant="default">{skill}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Education */}
                {properties.education && properties.education.length > 0 && (
                  <div className={properties.military_service ? "border-b border-border" : ""}>
                    <button
                      onClick={() => toggleSection('education')}
                      className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity mb-2 mt-2"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <GraduationCap className="h-5 w-5" />
                        השכלה
                      </div>
                      {isExpanded('education') ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded('education') && (
                      <div className="mt-2">
                        <ul className="space-y-2">
                          {properties.education.map((edu, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-sm">{edu}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Military Service */}
                {properties.military_service && (properties.military_service.role || properties.military_service.description) && (
                  <div>
                    <button
                      onClick={() => toggleSection('military_service')}
                      className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity mb-2 mt-2"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <Shield className="h-5 w-5" />
                        שירות צבאי
                      </div>
                      {isExpanded('military_service') ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded('military_service') && (
                      <div className="mt-2 space-y-2">
                        {properties.military_service.role && (
                          <p className="font-semibold">{properties.military_service.role}</p>
                        )}
                        {properties.military_service.description && (
                          <p className="text-sm whitespace-pre-wrap">{properties.military_service.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Professional Goals */}
          {properties.professional_goals && (
            <Card>
              <CardHeader>
                <button
                  onClick={() => toggleSection('professional_goals')}
                  className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    יעדים מקצועיים
                  </CardTitle>
                  {isExpanded('professional_goals') ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>
              </CardHeader>
              {isExpanded('professional_goals') && (
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{properties.professional_goals}</p>
                </CardContent>
              )}
            </Card>
          )}
        </>
      )}

      {/* Review Section */}
      <Card>
        <CardHeader>
          <button
            onClick={() => toggleSection('review')}
            className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
          >
            <CardTitle className="flex items-center gap-2">
              {sent_form.complete === 1 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <Clock className="h-5 w-5 text-muted-foreground" />
              )}
              ביקורת והערכה
            </CardTitle>
            {isExpanded('review') ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </CardHeader>
        {isExpanded('review') && (
          <CardContent className="space-y-4">
          <div>
            <span className="font-medium">תיאור משרה: </span>
            <span className="text-sm">{sent_form.job_description || '—'}</span>
          </div>
          {sent_form.score !== null && sent_form.score !== undefined && (
            <div>
              <span className="font-medium">ציון: </span>
              <Badge variant="default" className="ml-2">
                {sent_form.score.toFixed(1)}/10
              </Badge>
            </div>
          )}
          <div>
            <span className="font-medium">סטטוס: </span>
            {sent_form.complete === 1 ? (
              <Badge variant="default" className="bg-green-500 hover:bg-green-600 ml-2">
                הושלם
              </Badge>
            ) : (
              <Badge variant="default" className="ml-2">
                בהמתנה
              </Badge>
            )}
          </div>
          {sent_form.review && (
            <div>
              <span className="font-medium block mb-2">ביקורת:</span>
              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm whitespace-pre-wrap">{sent_form.review}</p>
              </div>
            </div>
          )}
          {!sent_form.review && sent_form.complete === 1 && (
            <p className="text-sm text-muted-foreground">אין ביקורת זמינה</p>
          )}
          {sent_form.complete !== 1 && (
            <p className="text-sm text-muted-foreground">הטופס עדיין לא הושלם</p>
          )}
          </CardContent>
        )}
      </Card>

      {/* Transcript Section - last on page */}
      <Card>
        <CardHeader>
          <button
            onClick={() => toggleSection('transcript')}
            className="w-full flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
          >
            <CardTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" />
              תמליל הטופס
            </CardTitle>
            {isExpanded('transcript') ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </CardHeader>
        {isExpanded('transcript') && (
          <CardContent>
            {sent_form.transcript ? (
              <div
                className="bg-muted p-4 rounded-md text-sm whitespace-pre-wrap [&_strong]:font-bold"
                dangerouslySetInnerHTML={{
                  __html: sent_form.transcript
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/\*\*נבחר\*\*/g, '<strong>נבחר</strong>')
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">אין תמליל זמין</p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  )
}
